import { createFileRoute, Link } from "@tanstack/react-router";
import { Card, CardContent } from "@simurgh/feathers/ui/card";
import { AnimatedNumber } from "@simurgh/feathers/ui/animated-number";
import { Cards } from ":widgets/deck/ui/cards";
import { AddCardsToDeckButton } from ":widgets/deck/ui/add-card-to-deck-button";
import { useCards } from ":features/free-spaced-repetition-scheduling/lib/use-cards";
import { buttonStyles } from "@simurgh/feathers/ui/button";
import { useCardsStats } from ":entities/card";
import type { DeckIdentifiable } from ":entities/deck";
import { AutocompleteTagField } from ":widgets/tag/ui/autocomplete-tag-field/tag-field";
import { useDeleteRecordsTagDeck, useRecordsTagDeck } from ":entities/tag-deck";
import { useCreateTag } from ":widgets/tag/ui/autocomplete-tag-field/use-get-or-create-tag";
import { Suspense } from "react";
import { CardListSkeleton } from ":shared/ui/skeleton";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/$locale/dashboard/decks/$deckId")({
	component: DeckDetailComponent,
});

function DeckDetailComponent() {
	const { t } = useTranslation();
	const { deckId, locale } = Route.useParams();

	const { data: cards } = useCards({ deckId, refetchOnMount: true });
	const stats = useCardsStats({ deckId });

	return (
		<>
			<title>
				{t("pages.deckDetail.title")} - {t("app.name")}
			</title>
			<meta name="description" content={t("pages.deckDetail.description")} />
			<div className="space-y-4">
				<section>
					<div className="flex items-center justify-between mb-4">
						<h2 className="text-lg font-medium">
							{t("pages.deckDetail.overview")}
						</h2>
						<Link
							className={buttonStyles({ intent: "primary" })}
							to="/$locale/dashboard/decks/$deckId/review"
							params={{ deckId, locale }}
						>
							{t("pages.deckDetail.review")}
						</Link>
					</div>
					<div className="flex items-center justify-between gap-3">
						<Card className="bg-warning text-warning-fg min-w-32 w-2/5">
							<CardContent>
								<div className="flex flex-col items-start">
									<span className="text-sm font-normal">
										{t("pages.deckDetail.due")}
									</span>
									<AnimatedNumber
										className="text-lg font-bold"
										value={stats?.dueCount ?? 0}
									/>
									<span className="text-sm font-normal">
										{t("pages.deckDetail.cards")}
									</span>
								</div>
							</CardContent>
						</Card>
						<Card className="bg-primary text-primary-fg w-full">
							<CardContent>
								<div className="flex flex-col items-start">
									<span className="text-sm font-normal">
										{t("pages.deckDetail.learning")}
									</span>
									<AnimatedNumber
										className="text-lg font-bold"
										value={stats?.learning ?? 0}
									/>
									<span className="text-sm font-normal">&nbsp;</span>
								</div>
							</CardContent>
						</Card>
						<Card className="bg-success text-success-fg w-full">
							<CardContent>
								<div className="flex flex-col items-start">
									<span className="text-sm font-normal">
										{t("pages.deckDetail.new")}
									</span>
									<AnimatedNumber
										className="text-lg font-bold"
										value={stats?.new ?? 0}
									/>
									<span className="text-sm font-normal">&nbsp;</span>
								</div>
							</CardContent>
						</Card>
					</div>
				</section>

				<DeckTagField deckId={deckId} />

				<section>
					<div className="flex items-center justify-between mb-4">
						<h2 className="text-lg font-medium">
							{t("pages.deckDetail.cards")}
						</h2>
						<AddCardsToDeckButton deckId={deckId} filterCards={cards} />
					</div>
					<Suspense fallback={<CardListSkeleton />}>
						<Cards cards={cards} deckId={deckId} />
					</Suspense>
				</section>
			</div>
		</>
	);
}

function DeckTagField({ deckId }: { deckId: DeckIdentifiable["id"] }) {
	const { t } = useTranslation();
	const { data } = useRecordsTagDeck({
		filters: { deckId: deckId },
	});
	const { mutate: onCreateTag } = useCreateTag();
	const { mutate: onDeleteRecordsById } = useDeleteRecordsTagDeck();

	return (
		<AutocompleteTagField
			label={t("forms.labels.stickTags")}
			className="flex-1"
			placeholder={t("forms.placeholders.typeToSearchSelectTags")}
			name="filterTags"
			selectedTags={(data ?? []).map((item) => item.tagId)}
			onCreate={({ tag }) => onCreateTag({ tag, deckId }) as any}
			onInsert={async (tag) => onCreateTag({ tag, deckId })}
			onRemove={async (tagId) =>
				onDeleteRecordsById({
					value: { tagId: [tagId], deckId: [deckId] },
					key: ["tagId", "deckId"],
				})
			}
		/>
	);
}
